import { useCallback, useEffect } from "react";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import { ErrorLike } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Button from "ds/components/Button";
import Box from "ds/components/Box";
import CodeSnippetInline from "ds/components/CodeSnippetInline";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";

import { SET_OIDC_SUBJECT_TEMPLATE, GET_OIDC_SUBJECT_TEMPLATE } from "./gql";
import {
  AVAILABLE_PLACEHOLDERS,
  DEFAULT_TEMPLATE,
  extractValidationError,
  getFormDefaultValues,
  OidcSubjectTemplateFormFields,
} from "./helpers";

type OidcSubjectTemplateFormProps = {
  currentTemplate: string | null | undefined;
};

const OidcSubjectTemplateForm = ({ currentTemplate }: OidcSubjectTemplateFormProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const form = useForm<OidcSubjectTemplateFormFields>({
    defaultValues: getFormDefaultValues(currentTemplate),
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    setError,
    clearErrors,
  } = form;

  useEffect(() => {
    reset(getFormDefaultValues(currentTemplate));
  }, [reset, currentTemplate]);

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const handleSaveError = useCallback(
    (error: ErrorLike) => {
      const validationMessage = extractValidationError(error);

      if (validationMessage) {
        setError("template", { message: validationMessage });
      }
    },
    [setError]
  );

  const [setTemplate, { loading: saveLoading }] = useMutation(SET_OIDC_SUBJECT_TEMPLATE, {
    refetchQueries: [GET_OIDC_SUBJECT_TEMPLATE],
    awaitRefetchQueries: true,
    onError: handleSaveError,
  });

  const onSubmit: SubmitHandler<OidcSubjectTemplateFormFields> = useCallback(
    async (formFields) => {
      const result = await setTemplate({
        variables: { template: formFields.template.trim() || null },
      });

      if (result.data) {
        reportSuccess({ message: "OIDC subject template has been updated" });
      }
    },
    [setTemplate, reportSuccess]
  );

  const handleTemplateChange = () => {
    if (errors.template) {
      clearErrors("template");
    }
  };

  const { onChange: registerOnChange, ...registerRest } = register("template");

  return (
    <FormProvider {...form}>
      <FormField
        label="Template"
        isOptional
        error={errors?.template?.message}
        helperText="Leave empty to use the default template."
        tooltipInfo={
          <>
            <TooltipModalTitle>OIDC subject template</TooltipModalTitle>
            <TooltipModalBody align="start">
              The subject template controls how the subject claim is generated in OIDC federation
              tokens used for cloud provider integration (AWS, GCP, Azure). You can use placeholders
              to include runtime values like space path, caller type, and run information. If no
              template is set, the default format is used.
            </TooltipModalBody>
            <TooltipModalBody align="start">
              <Box direction="column" gap="medium">
                <Typography tag="span" variant="p-t7">
                  Available placeholders
                </Typography>
                <Box gap="small" wrap>
                  {AVAILABLE_PLACEHOLDERS.map((placeholder) => (
                    <CodeSnippetInline key={placeholder}>{placeholder}</CodeSnippetInline>
                  ))}
                </Box>
              </Box>
            </TooltipModalBody>
          </>
        }
        tooltipInfoVariant="modal"
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder={DEFAULT_TEMPLATE}
            {...registerRest}
            onChange={(e) => {
              handleTemplateChange();
              registerOnChange(e);
            }}
            error={!!errors?.template}
            {...ariaInputProps}
          />
        )}
      </FormField>
      <Box justify="end" gap="large" margin="x-large 0 0">
        {isDirty && (
          <Button variant="secondary" onPress={() => reset()} disabled={saveLoading}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          onPress={() => handleSubmit(onSubmit)()}
          loading={saveLoading}
          disabled={saveLoading}
        >
          Save
        </Button>
      </Box>
    </FormProvider>
  );
};

export default OidcSubjectTemplateForm;
